import { formatISO, startOfDay } from 'date-fns';
import { parseDate } from '../../../../../utils/dateUtils';
import {
  GeneratedAbsence,
  GeneratedApprovalRequest,
  GeneratedApprover,
  GeneratedApproverAvatar,
  ApprovalRequest,
  Absence,
  ApproverAvatar,
  Approver,
} from './MyAbsenceFilters/types';
import i18next from 'i18next';
import { mapLoomVideo } from '../../../components/LoomVideo';
import { AbsenceType } from '../../../__generated__/graphql';
import { DeleteAbsenceDisabledDetails } from './types';
import { Maybe } from 'graphql/jsutils/Maybe';

export const mapAvatar = (src: GeneratedApproverAvatar): ApproverAvatar => ({
  id: src?.Id,
  name: src?.Name,
  url: src?.url,
});

export const mapApprover = (src: GeneratedApprover): Maybe<Approver> => {
  if (!src) {
    return;
  }

  return {
    id: src.Id,
    name: src.Name,
    firstName: src.flair__First_Name__c,
    lastName: src.flair__Last_Name__c,
    avatar: mapAvatar(src.avatar),
  };
};

export const mapApprovalRequests = (
  src: ReadonlyArray<GeneratedApprovalRequest> | null,
): ApprovalRequest[] => {
  return src
    ? src
        .filter((item) => item.flair__Approver__c && item.approver)
        .map((item) => ({
          id: item.Id,
          approvalStatus: item.flair__Approval_Status__c,
          approverId: item.flair__Approver__c!,
          approver: mapApprover(item.approver)!,
          approverComment: item.flair__Approver_Comment__c,
          lastModifiedDate: item.LastModifiedDate,
          relatedId: item.flair__Related_Id__c,
        }))
    : [];
};

export const mapAbsences = (
  now: Date,
  src: ReadonlyArray<GeneratedAbsence>,
): Absence[] => {
  return src.map((item) => mapAbsence(now, item));
};

const mapAbsence = (now: Date, item: GeneratedAbsence): Absence => {
  const isHourly = item.flair__Type__c === AbsenceType.Hourly;
  const isMultiDay = item.flair__Start_Date__c !== item.flair__End_Date__c;

  return {
    ...item,
    id: item.Id,
    absenceApprovalStatus: item.flair__Approval_Status__c,
    categoryName: item.flair__Category_Name__c,
    requestPolicyName: item.flair__Request_Policy_Name__c,
    categoryIcon: item.flair__Category_Icon__c,
    categoryColor: item.flair__Category_Color__c,
    amount: item.flair__Amount__c,
    withdrawalAmount: item.flair__Withdrawal_Amount__c,
    startDate: item.flair__Start_Date__c,
    startTime: item.flair__Start_Time__c,
    startDateHalfDay: item.flair__Start_Date_Half_Day__c,
    endDate: item.flair__End_Date__c,
    endTime: item.flair__End_Time__c,
    endDateHalfDay: item.flair__End_Date_Half_Day__c,
    categoryRestrictAbsenceDeletion:
      item.flair__Category_Restrict_Absence_Deletion__c ?? false,
    type: item.flair__Type__c,
    createdDate: item.CreatedDate ?? formatISO(now),
    approvalRequests: mapApprovalRequests(item.approvalRequests),
    recordId: item.Id,
    commentsCount: item.commentsCount,
    loomVideo: mapLoomVideo(item.loomVideo),
    isHourly,
    isMultiDay,
    ...mapDeleteDisabled(now, item.flair__Start_Date__c),
  };
};

export const mapDeleteDisabled = (
  now: Date,
  startDate: string,
): DeleteAbsenceDisabledDetails => {
  if (startOfDay(parseDate(startDate)) <= startOfDay(now)) {
    return {
      deleteDisabled: {
        reason: i18next.t(
          'absences.cards.upcomingAbsences.actions.deleteDisabledReason',
        ),
      },
    };
  }
  return {};
};
